/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types, flow, getRoot } from 'mobx-state-tree';
import {
  getGlossaries,
  uploadGlossary,
  removeGlossary,
  getGlossaryUploadGlobalErrorDialogContent
} from '../services/glossaryService';

const Glossary = types.model('Glossary', {
  datetime: types.maybeNull(types.string),
  glossary_filename: types.maybeNull(types.string),
  glossary_id: types.identifier,
  services: types.array(types.string),
  shared_by: types.maybeNull(types.string),
  source_lang: types.maybeNull(types.string),
  target_lang: types.maybeNull(types.string)
});

export default types
  .model('GlossaryStore', {
    glossaries: types.array(Glossary),
    isLoading: types.optional(types.boolean, false),
    isLoaded: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    isUploading: types.optional(types.boolean, false),
    uploadError: types.maybeNull(types.string),
    selectedGlossaryId: types.maybeNull(types.string)
  })
  .views(self => ({
    get glossariesByLanguage() {
      // Group glossaries by source and target language
      const grouped = {};
      self.glossaries.forEach(glossary => {
        const key = `${glossary.source_lang}-${glossary.target_lang}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(glossary);
      });
      return grouped;
    },

    get glossariesCount() {
      return self.glossaries.length;
    },

    get selectedGlossary() {
      return self.selectedGlossaryId
        ? self.glossaries.find(
            glossary => glossary.glossary_id === self.selectedGlossaryId
          )
        : null;
    }
  }))
  .actions(self => ({
    loadGlossaries: flow(function* loadGlossaries() {
      self.isLoading = true;
      self.error = null;

      try {
        const response = yield getGlossaries();

        // Clear existing glossaries
        self.glossaries.clear();

        // Add new glossaries
        if (response && Array.isArray(response.data)) {
          response.data.forEach(glossaryData => {
            self.glossaries.push(Glossary.create(glossaryData));
          });
        }

        self.isLoaded = true;
      } catch (error) {
        self.error = error.message || 'Failed to load glossaries';
      } finally {
        self.isLoading = false;
      }
    }),

    clearGlossaries() {
      self.glossaries.clear();
      self.isLoaded = false;
      self.error = null;
    },

    addGlossary(glossaryData) {
      self.glossaries.push(Glossary.create(glossaryData));
    },

    uploadGlossaryFile: flow(function* uploadGlossaryFile(
      file,
      customName = null
    ) {
      self.isUploading = true;
      self.uploadError = null;

      try {
        let fileToUpload = file;

        // Create new File object with custom name if provided
        if (customName) {
          // More robust extension extraction
          const fileName = file.name || '';
          const lastDotIndex = fileName.lastIndexOf('.');
          const fileExtension =
            lastDotIndex > 0 && lastDotIndex < fileName.length - 1
              ? fileName.substring(lastDotIndex + 1)
              : '';
          const newFileName = `${customName}.${fileExtension}`;

          fileToUpload = new File([file], newFileName, { type: file.type });
        }

        const response = yield uploadGlossary(fileToUpload);

        // True server 500 (no details.message); remove once validation uses 400/422.
        if (response.showGlobalError) {
          const { MessageDialog } = getRoot(self);
          const { message, title } =
            getGlossaryUploadGlobalErrorDialogContent(response);
          MessageDialog.showErrorMessageDialog(message, title);
          return null;
        }

        if (response && response.success) {
          // Reload glossaries to get the updated list from server
          yield self.loadGlossaries();
          return response.data;
        }

        if (response?.inlineMessage) {
          // Includes validation errors returned as 500 + details.message today.
          throw new Error(response.inlineMessage);
        }

        throw new Error('Upload failed');
      } catch (error) {
        self.uploadError = error.message || 'Failed to upload glossary';
        throw error;
      } finally {
        self.isUploading = false;
      }
    }),

    clearUploadError() {
      self.uploadError = null;
    },

    setSelectedGlossary(glossaryId) {
      self.selectedGlossaryId = glossaryId;
    },

    clearSelectedGlossary() {
      self.selectedGlossaryId = null;
    },

    removeGlossaryById: flow(function* removeGlossaryById(glossaryId) {
      self.isLoading = true;
      self.error = null;

      try {
        yield removeGlossary(glossaryId);

        // Remove glossary from local store
        const glossaryIndex = self.glossaries.findIndex(
          glossary => glossary.glossary_id === glossaryId
        );

        if (glossaryIndex !== -1) {
          self.glossaries.splice(glossaryIndex, 1);
        }

        // Clear selection if deleted glossary was selected
        if (self.selectedGlossaryId === glossaryId) {
          self.selectedGlossaryId = null;
        }
      } catch (error) {
        self.error = error.message || 'Failed to remove glossary';
        throw error;
      } finally {
        self.isLoading = false;
      }
    })
  }));
